import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Spinner, Dropdown, Button, Container, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Input, Empty, Select } from "antd";

import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import useUsers from "hooks/userManagement/useUsers";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import Guard from "components/access-control/rbac/Guard";

import useAffiliations from "hooks/userManagement/useAffiliations";
import AddAffiliationForm from "./AddAffiliationForm";
import useDebounce from "hooks/route-rules/useDebounce";

const STATUS_COLORS = {
  ACTIVE: "success",
  PENDING: "warning",
  DISABLED: "secondary",
  SUSPENDED: "warning",
  DELETED: "danger",
  UNKNOWN: "secondary",
};

const STATUS_FILTER_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Pending", value: "PENDING" },
  { label: "Disabled", value: "DISABLED" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Deleted", value: "DELETED" },
  { label: "Unknown", value: "UNKNOWN" },
];

const Users = ({ staff = false }) => {
  const navigate = useNavigate();

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Assign affiliation state
  const [showAddModal, setShowAddModal] = useState(false);
  const [userForAffiliation, setUserForAffiliation] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchActive = debouncedSearchTerm.trim().length >= 3;

  // Full page loader on first mount
  const [firstLoad, setFirstLoad] = useState(true);

  // Users hook
  const {
    users,
    loading,
    error,
    totalPages,
    totalElements,
    refetch,
    searchUserProfiles,
    getUserById,
    deleteUserProfileProperty,
  } = useUsers(page, pageSize, staff);

  const { assignAffiliationToUser } = useAffiliations();

  // Reset page on search change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStatus]);

  // Fetch data (first load + normal paging)
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSearchActive) {
          await searchUserProfiles(debouncedSearchTerm, selectedStatus, page);
        } else {
          await refetch();
        }
      } finally {
        setFirstLoad(false);
      }
    };
    fetchData();
  }, [
    debouncedSearchTerm,
    page,
    isSearchActive,
    searchUserProfiles,
    selectedStatus,
    refetch,
  ]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetch]);

  // Format text – truncate email to 8 chars
  const formatText = (text) => (text ? `${text.slice(0, 8)}...` : "None");
  const formatFull = (val) => val || "None";

  // Action handlers
  const handleViewUser = useCallback(
    (id) => {
      navigate(`/olitask/users/${id}`);
    },
    [navigate],
  );

  const handleEditUser = useCallback(
    async (id) => {
      try {
        await getUserById(id);
        navigate(`/olitask/users/${id}/edit`);
      } catch {}
    },
    [getUserById, navigate],
  );

  const confirmDelete = (id) => {
    setUserToDelete(id);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await deleteUserProfileProperty(userToDelete);
      toast.success("User profile deleted successfully.");
      await refetch();
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete user profile.";
      const hasLinkedRecords = /linked|constraint|foreign key|referenced/i.test(
        apiMessage,
      );

      if (hasLinkedRecords) {
        const msg =
          "This user has linked records. Remove related records first.";
        setDeleteError(msg);
        toast.info(msg);
      } else {
        setDeleteError(apiMessage);
        toast.error(apiMessage);
      }
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleting(false);
    }
  };

  const handleAddAffiliation = async (affiliationData) => {
    if (!userForAffiliation) {
      toast.error("User ID is missing.");
      return;
    }

    try {
      await assignAffiliationToUser(userForAffiliation, affiliationData);
      refetch();
      toast.success("Affiliation added successfully.");
    } catch {
      toast.error("Failed to add affiliation.");
    } finally {
      setShowAddModal(false);
      setUserForAffiliation(null);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setUserForAffiliation(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteError(null);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "User ID",
        cell: ({ getValue }) => formatFull(getValue()),
      },

      {
        accessorFn: (row) =>
          `${row.firstName || ""} ${row.lastName || ""}`.trim() || "None",
        id: "fullName",
        header: "Name",
        cell: ({ getValue }) => (
          <div
            style={{
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={getValue()}
          >
            {getValue()}
          </div>
        ),
      },

      {
        accessorKey: "email",
        header: "Email",
        cell: ({ getValue }) => getValue(),
      },
      {
        accessorKey: "gender",
        header: "Gender",
        cell: ({ getValue }) => formatFull(getValue()),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() || "UNKNOWN";
          const color = STATUS_COLORS[status] || "secondary";
          return <SubtleBadge bg={color}>{status}</SubtleBadge>;
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const user = row.original;
          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleViewUser(user.id)}>
                  View Details
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() =>
                    navigate(`/olitask/users/${user.id}/actionables`)
                  }
                >
                  Actionables
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setUserForAffiliation(user.id);
                    setShowAddModal(true);
                  }}
                >
                  Assign Affiliation
                </Dropdown.Item>
                <Guard privilege="UPDATE_USERS">
                  <Dropdown.Item onClick={() => handleEditUser(user.id)}>
                    Edit
                  </Dropdown.Item>
                </Guard>
                <Guard privilege="DELETE_USERS">
                  <Dropdown.Divider />
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => confirmDelete(user.id)}
                  >
                    Delete
                  </Dropdown.Item>
                </Guard>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [handleViewUser, handleEditUser],
  );

  const tableData = useMemo(() => {
    const data = Array.isArray(users) ? users : [];
    if (selectedStatus === "ALL") return data;
    return data.filter(
      (user) => (user?.status || "UNKNOWN") === selectedStatus,
    );
  }, [users, selectedStatus]);

  const table = useAdvanceTable({
    data: tableData,
    columns,
    sortable: true,
    pagination: true,
    perPage: 50,
  });

  return (
    <>
      <WidgetSectionTitle
        className="mb-3 mt-3"
        title={staff ? "Staff Profiles" : "Customer Profiles"}
        icon="users"
        subtitle={`Manage ${staff ? "staff" : "customer"} user profiles.`}
        transform="shrink-2"
      />

      {/* Search + Add button */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center mb-3 gap-2">
        <div className="d-flex flex-column flex-sm-row gap-2 flex-grow-1">
          <div className="flex-grow-1" style={{ maxWidth: 300 }}>
            <Input
              placeholder="Search by name, email, or ID (min. 3 chars)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div className="flex-grow-1 d-flex justify-content-center">
          <div style={{ minWidth: 180, maxWidth: 220 }}>
            <Select
              style={{ width: "100%" }}
              value={selectedStatus}
              showSearch={{
                optionFilterProp: "label",
              }}
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 240 } } }}
              onChange={(value) => {
                setSelectedStatus(value);
                setPage(0);
              }}
              options={STATUS_FILTER_OPTIONS}
            />
          </div>
        </div>
        <div className="mt-2 mt-sm-0">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            transform="shrink-3"
            onClick={() => navigate(`/olitask/users/add?staff=${staff}`)}
          >
            <span className="ms-1">Add New {staff ? "Staff" : "Customer"}</span>
          </IconButton>
        </div>
      </div>

      {/* Main table / loader */}
      {firstLoad ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Spinner animation="border" variant="success" />
          <div className="mt-2">Loading user profiles...</div>
        </div>
      ) : loading && isSearchActive ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="success" />
        </div>
      ) : tableData.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty
            description={`No ${staff ? "staff" : "customer"} profiles found`}
          />
        </div>
      ) : (
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-300 text-nowrap align-middle text-black"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={() => setPage((p) => Math.max(p - 1, 0))}
                onNext={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
              />
            </div>
          </AdvanceTableProvider>
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-danger mt-2">{error}</div>
      )}

      {/* Assign Affiliation Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        size="md"
      >
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">Assign Affiliation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <AddAffiliationForm
            userId={userForAffiliation}
            onAdded={handleAddAffiliation}
            onClose={handleCloseAddModal}
          />
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete this user profile? This action
            cannot be undone.
          </p>
          {deleteError && (
            <div className="alert alert-warning mt-3">{deleteError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancelDelete}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
